import React, { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from "react-router-dom";
import { mixPostV1, mixGetV1 } from "services/mixin";
import use13WAccess from "hooks/use13WAccess";

interface SidebarProps {
  isSidebarCollapsed: boolean;
  setIsSidebarCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
}

const isUserAdminOrSuperAdmin = (u: any): boolean => {
  if (!u) return false;

  let isAdmin = false;

  if (Array.isArray(u.roles)) {
    const hasAdmin = u.roles.some((r: any) => {
      const name = String(r?.name || '').toLowerCase();
      return name === 'admin' || name === 'super_admin';
    });
    if (hasAdmin) isAdmin = true;
  }

  if (!isAdmin && Array.isArray(u.assigned_roles)) {
    const hasAdminAssigned = u.assigned_roles.some((r: any) => {
      const name = String(r?.name || '').toLowerCase();
      return name === 'admin' || name === 'super_admin';
    });
    if (hasAdminAssigned) isAdmin = true;
  }

  if (!isAdmin && Array.isArray(u.role_names)) {
    const hasAdminName = u.role_names.some((r: any) => {
      const name = String(r || '').toLowerCase();
      return (
        name.includes('super admin') ||
        name.includes('admin') ||
        name.includes('administrator')
      );
    });
    if (hasAdminName) isAdmin = true;
  }

  if (!isAdmin && typeof u.role === 'string') {
    const r = u.role.toLowerCase();
    if (r === 'admin' || r === 'super_admin') isAdmin = true;
  }

  if (!isAdmin && typeof u.roles_display === 'string') {
    const rd = u.roles_display.toLowerCase();
    if (rd.includes('admin') || rd.includes('super_admin') || rd.includes('administrator')) isAdmin = true;
  }

  if (isAdmin) {
    localStorage.setItem('is_admin', 'true');
    return true;
  }

  // Fallback: nếu localStorage đã lưu is_admin = true (đề phòng trường hợp API update user vô tình làm mất field roles)
  if (localStorage.getItem('is_admin') === 'true' && (u.id || localStorage.getItem('token'))) {
    return true;
  }

  return false;
};

const Sidebar: React.FC<SidebarProps> = ({ isSidebarCollapsed, setIsSidebarCollapsed }) => {
  const { access, canAccess } = use13WAccess();
  // const purchased = true;
  const { t, i18n } = useTranslation();
  const [currentUser, setCurrentUser] = useState<any>(() => {
    try {
      return JSON.parse(localStorage.getItem('user') || '{}');
    } catch {
      return {};
    }
  });

  const isAdmin = isUserAdminOrSuperAdmin(currentUser);
  const currentLanguage = i18n.language || currentUser.language || localStorage.getItem('i18nextLng') || 'en';
  const lng = currentLanguage.startsWith('vi') ? 'vi' : 'en';
  const location = useLocation();

  // Tự động khôi phục / đồng bộ full user profile (kèm roles) từ backend nếu user đã đăng nhập
  useEffect(() => {
    if (currentUser?.id) {
      mixGetV1(`/api/v1/be/user/${currentUser.id}`)
        .then((res: any) => {
          if (res?.status && res?.data) {
            const merged = {
              ...currentUser,
              ...res.data,
              roles: (Array.isArray(res.data.roles) && res.data.roles.length > 0) ? res.data.roles : currentUser.roles,
              assigned_roles: (Array.isArray(res.data.assigned_roles) && res.data.assigned_roles.length > 0) ? res.data.assigned_roles : currentUser.assigned_roles,
              role_names: (Array.isArray(res.data.role_names) && res.data.role_names.length > 0) ? res.data.role_names : currentUser.role_names,
              role: res.data.role || currentUser.role,
              roles_display: res.data.roles_display || currentUser.roles_display,
            };
            localStorage.setItem('user', JSON.stringify(merged));
            setCurrentUser(merged);
            if (isUserAdminOrSuperAdmin(merged)) {
              localStorage.setItem('is_admin', 'true');
            }
          }
        })
        .catch((err: any) => {
          console.error("Error refreshing user profile in Sidebar:", err);
        });
    }
  }, [currentUser?.id]);

  const [isDarkMode, setIsDarkMode] = useState(localStorage.getItem("theme") === "dark");

  useEffect(() => {
    const savedTheme = localStorage.getItem("theme");
    const htmlTag = document.documentElement;

    if (savedTheme === "dark") {
      htmlTag.classList.add("theme-dark");
      setIsDarkMode(true);
    } else {
      htmlTag.classList.remove("theme-dark");
      setIsDarkMode(false);
    }
  }, []);

  const toggleTheme = (e?: React.MouseEvent) => {
    if (e) e.preventDefault();
    const htmlTag = document.documentElement; // tương đương <html>
    htmlTag.classList.toggle("theme-dark");
    const newTheme = htmlTag.classList.contains("theme-dark") ? "dark" : "light";
    localStorage.setItem("theme", newTheme);
    setIsDarkMode(newTheme === "dark");
    window.dispatchEvent(new Event('themeChanged'));
  };

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  const [isOpen, setIsOpen] = useState(false);
  const [isOpen13W, setIsOpen13W] = useState(
    location.pathname === '/academy' || location.pathname.startsWith('/academy/quiz')
  );
  const [isOpenCommunity, setIsOpenCommunity] = useState(
    location.pathname === '/community' || location.pathname.startsWith('/community/')
  );
  const [isOpenSP, setIsOpenSP] = useState(false);
  const [isOpenUD, setIsOpenUD] = useState(false);
  const [isOpenLang, setIsOpenLang] = useState(false);
  const [isOpenNum, setisOpenNum] = useState(false);

  useEffect(() => {
    if (location.pathname === '/academy' || location.pathname.startsWith('/academy/quiz')) setIsOpen13W(true);
    if (location.pathname === '/community' || location.pathname.startsWith('/community/')) setIsOpenCommunity(true);
  }, [location.pathname]);

  const toggleCollapse13W = () => {
    setIsOpen13W((prev) => !prev);
  };

  const toggleCollapseLang = () => {
    setIsOpenLang((prev) => !prev);
  };
  const toggleCollapseNum = () => {
    setisOpenNum((prev) => !prev);
  };

  const changeLanguage = async (newLng: string) => {
    const curUser = JSON.parse(localStorage.getItem('user') || '{}');
    const wasAdmin = isUserAdminOrSuperAdmin(curUser);

    // 1. Cập nhật ngay ngôn ngữ trong localStorage và i18n, BẢO TOÀN toàn bộ roles và thông tin cũ
    const updatedUser = {
      ...curUser,
      language: newLng,
    };
    if (wasAdmin) {
      localStorage.setItem('is_admin', 'true');
    }
    localStorage.setItem('user', JSON.stringify(updatedUser));
    localStorage.setItem('i18nextLng', newLng);

    // 2. Gửi request cập nhật ngôn ngữ lên server nếu user có id
    if (curUser?.id) {
      try {
        const res = await mixPostV1(`/api/v1/be/user/${curUser.id}`, { language: newLng, _method: 'PUT' });
        if (res?.status) {
          // Lấy lại full user profile (có quan hệ roles) từ GET API thay vì ghi đè bằng res.data thiếu roles
          const res2 = await mixGetV1(`/api/v1/be/user/${curUser.id}`);
          if (res2?.status && res2?.data) {
            const fullMerged = {
              ...curUser,
              ...res2.data,
              language: newLng,
              roles: (Array.isArray(res2.data.roles) && res2.data.roles.length > 0) ? res2.data.roles : curUser.roles,
              assigned_roles: (Array.isArray(res2.data.assigned_roles) && res2.data.assigned_roles.length > 0) ? res2.data.assigned_roles : curUser.assigned_roles,
              role_names: (Array.isArray(res2.data.role_names) && res2.data.role_names.length > 0) ? res2.data.role_names : curUser.role_names,
              role: res2.data.role || curUser.role,
              roles_display: res2.data.roles_display || curUser.roles_display,
            };
            localStorage.setItem('user', JSON.stringify(fullMerged));
            if (isUserAdminOrSuperAdmin(fullMerged)) {
              localStorage.setItem('is_admin', 'true');
            }
          }
        }
      } catch (err) {
        console.error("Error updating language on backend:", err);
      }
    }

    // 3. Reload trang để áp dụng ngôn ngữ mới
    window.location.reload();
  };

  return (
    <div id="sidebar-main" className={`sidebar sidebar--expand-lg ${isSidebarCollapsed ? 'sidebar--collapsed' : ''}`}>
      <div className="sidebar__content">
        {/* Header với toggle button */}
        <div className="sidebar__header">
          <div className="sidebar__header-content">
            {!isSidebarCollapsed && (
              <div className="sidebar__heading">
                {t('Main menu')}
              </div>
            )}
            {/* Toggle button */}
            <button
              className="sidebar__toggle-btn"
              onClick={toggleSidebar}
              title={isSidebarCollapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                {isSidebarCollapsed ? (
                  // Icon để mở rộng (chevron right)
                  <path d="m9 18 6-6-6-6" />
                ) : (
                  // Icon để thu gọn (chevron left) 
                  <path d="m15 18-6-6 6-6" />
                )}
              </svg>
            </button>
          </div>
        </div>

        <div className="sidebar__links-wrapper">
          <ul className="sidebar__links">
            {!isSidebarCollapsed && (
              <li className="sidebar__item">
                <div className="sidebar__separator ">
                </div>
              </li>
            )}
            <li className="sidebar__item">
              <a
                className={`sidebar__link${location.pathname === "/accounts-overview" ||
                  location.pathname.includes("mt5") ||
                  location.pathname.includes("ctrader")
                  ? " active"
                  : ""
                  }`}
                data-testid="navAccountsOverview"
                id="menuAccountsOverview"
                href="/accounts-overview"
              >
                <div className="icon-link">
                  <i className="icon-link__icon fi fi--home"></i>
                  <span className="icon-link__text">{t('Accounts Overview')}</span>
                </div>
              </a>
            </li>

            {/* <li>
              <a className="sidebar__link " data-testid="navClientAnalysis" id="menuClientAnalysis" href="/accounts-overview/matrix-risk-reward">
                <div className="icon-link">
                  <i className="icon-link__icon fi fi--person-in-loupe"></i>
                  <span className="icon-link__text">MaxTrix Risk Reward</span>
                </div>
              </a>
            </li> */}

            {/* <li className="sidebar__item">
              <div
                className={`sidebar__link ${isOpenNum ? "" : "collapsed"}`}
                onClick={toggleCollapseNum}
                data-bs-toggle="collapse"
              >
                <div className="sidebar__collapse-heading">{t('Trader Identity Blueprint')}</div>
              </div>
              <div className={`sidebar__collapse ${isOpenNum ? "show" : ""}`}>
                <ul className="collapse__links">
                  
                </ul>
              </div>
            </li> */}




            {/* <li className="sidebar__item"><a className={`sidebar__link`} data-testid="navPremium" id="menuPremium" href="/numerologist">
              <div className="icon-link">
                <i className="icon-link__icon fi fi--premium"></i>
                <span className="icon-link__text">Trader Identity Blueprint</span>
              </div>

            </a></li> */}
            {/* <li>
              <a className={`sidebar__link${location.pathname === "/numerologist" ? " active" : ""}`} data-testid="navNumerologist" id="menuNumerologist" data-bs-toggle="tooltip" data-bs-custom-class="tooltip--sidebar-item" data-bs-placement="right" href="/numerologist">
                <div className="icon-link">
                  <i className="icon-link__icon fi fi--premium"></i>
                  <span className="icon-link__text">Trader Identity Blueprint</span>
                </div>
                <i className="link-tooltip link-tooltip--locked"></i>
              </a>
            </li> */}
            <li className="sidebar__item">
              <div className="sidebar__separator "></div>
            </li>
            <li>
              <Link className={`sidebar__link${location.pathname === "/numerologist" ? " active" : ""}`} data-testid="navNumerologist" id="menuNumerologist" data-bs-toggle="tooltip" data-bs-custom-class="tooltip--sidebar-item" data-bs-placement="right" to="/numerologist">
                <div className="icon-link">
                  <i className="icon-link__icon fi fi--ecal"></i>
                  <span className="icon-link__text">{t('My Daily Readings')}</span>
                </div>
              </Link>
              <Link className={`sidebar__link${location.pathname === "/TBI" ? " active" : ""}`} data-testid="navTicker" id="menuTicker" to="/TBI">
                <div className="icon-link">
                  <i className="icon-link__icon fi fi--ticker"></i>
                  <span className="icon-link__text">{t('Trader DNA Profile')}</span>
                </div>
              </Link>
              <Link className={`sidebar__link${location.pathname === "/numerologist-products" ? " active" : ""}`} data-testid="navNumerologistproduct" id="menuNumerologistproduct" data-bs-toggle="tooltip" data-bs-custom-class="tooltip--sidebar-item" data-bs-placement="right" to="/numerologist-products">
                <div className="icon-link">
                  <i className="icon-link__icon fi fi--list-reload"></i>
                  <span className="icon-link__text">{t('Your Recommendations')}</span>
                </div>
              </Link>
            </li>
            <li>
              <a className={`sidebar__link${location.pathname === "/equity-simulator" ? " active" : ""}`} data-testid="navEquitySimulator" id="menuEquitySimulator" data-bs-toggle="tooltip" data-bs-custom-class="tooltip--sidebar-item" data-bs-placement="right" href="/equity-simulator">
                <div className="icon-link">
                  <i className="icon-link__icon fi fi--stats-laptop"></i>
                  <span className="icon-link__text">{t('Equity Simulator')}</span>
                </div>
                <i className="link-tooltip link-tooltip--locked"></i>
              </a>
            </li>
            <li className="sidebar__item">
              <Link className={`sidebar__link${location.pathname === "/profile" ? " active" : ""}`} data-testid="navProfile" data-notification-target="3" id="menuProfile" to="/profile">
                <div className="icon-link">
                  <i className="icon-link__icon fi fi--user"></i>
                  <span className="icon-link__text">{t('Profile')}</span>
                </div>
              </Link>
            </li>
            <li className="sidebar__item">
              <Link className={`sidebar__link${location.pathname === "/teams" ? " active" : ""}`} data-testid="navTeams" id="menuTeams" to="/teams">
                <div className="icon-link">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" className="bi bi-people" viewBox="0 0 16 16">
                    <path d="M15 14s1 0 1-1-1-4-5-4-5 3-5 4 1 1 1 1zm-7.978-1L7 12.996c.001-.264.167-1.03.76-1.72C8.312 10.629 9.282 10 11 10c1.717 0 2.687.63 3.24 1.276.593.69.758 1.457.76 1.72l-.008.002-.014.002zM11 7a2 2 0 1 0 0-4 2 2 0 0 0 0 4m3-2a3 3 0 1 1-6 0 3 3 0 0 1 6 0M6.936 9.28a6 6 0 0 0-1.23-.247A7 7 0 0 0 5 9c-4 0-5 3-5 4q0 1 1 1h4.216A2.24 2.24 0 0 1 5 13c0-1.01.377-2.042 1.09-2.904.243-.294.526-.569.846-.816M4.92 10A5.5 5.5 0 0 0 4 13H1c0-.26.164-1.03.76-1.724.545-.636 1.492-1.256 3.16-1.275ZM1.5 5.5a3 3 0 1 1 6 0 3 3 0 0 1-6 0m3-2a2 2 0 1 0 0 4 2 2 0 0 0 0-4" />
                  </svg>
                  <span className="icon-link__text">{t('Teams')}</span>
                </div>
              </Link>
            </li>
            {
              access ? (
                <li className="sidebar__item">
                  <div className={`sidebar__link sidebar__link--with-toggle ${location.pathname === "/academy" || location.pathname.startsWith("/academy/quiz") ? " active" : ""}`}>
                    <Link
                      className="icon-link"
                      to="/academy?tab=overview"
                      style={{ flex: 1, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 'inherit' }}
                    >
                      <i className="icon-link__icon fi fi--tutor"></i>
                      <span className="icon-link__text">{t('LUMIR Academy')}</span>
                    </Link>
                    <button
                      className={`sidebar__toggle-13w${isOpen13W ? ' sidebar__toggle-13w--open' : ''}`}
                      onClick={toggleCollapse13W}
                      title={isOpen13W ? 'Đóng menu' : 'Mở menu'}
                    >
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="m3 2 4 4-4 4" />
                      </svg>
                    </button>
                  </div>
                  <div className={`sidebar__collapse ${isOpen13W ? "show" : ""}`}>
                    <ul className="collapse__links">
                      <li className="collapse__item">
                        {[
                          // { tab: 'overview',  labelVi: 'Tổng quan',          labelEn: 'Overview',         svg: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{display:'block'}}><circle cx="12" cy="12" r="10"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/><line x1="2" y1="12" x2="22" y2="12"/></svg> },
                          //{ tab: 'dashboard', labelVi: 'Dashboard', labelEn: 'Dashboard', svg: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /></svg> },
                          //{ tab: 'roadmap', labelVi: 'Lộ trình 12 tháng', labelEn: '12-Month Roadmap', svg: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}><path d="M3 17l4-8 4 4 4-6 4 5" /><path d="M3 21h18" /></svg> },
                          //{ tab: 'kpi', labelVi: 'KPI Tinh hoa', labelEn: 'Elite KPIs', svg: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}><line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" /></svg> },
                          { tab: 'checklist', labelVi: 'Checklist hàng ngày', labelEn: 'Daily Checklist', svg: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}><path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></svg> },
                          //{ tab: 'eval',      labelVi: 'Tự đánh giá Elite',   labelEn: 'Elite Self-Eval',  svg: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{display:'block'}}><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg> },
                          //{ tab: 'eliteplus', labelVi: 'Lộ trình Elite+',     labelEn: 'Elite+ Roadmap',   svg: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{display:'block'}}><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg> },
                          { tab: 'quiz', path: '/academy/quiz/dashboard', labelVi: 'Trắc Nghiệm', labelEn: 'Quiz', svg: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}><path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></svg> },
                          { tab: 'handbook', labelVi: 'Cẩm nang sử dụng', labelEn: 'User Handbook', svg: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" /><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" /></svg> },
                        ].map(({ tab, path, labelVi, labelEn, svg }: { tab: string; path?: string; labelVi: string; labelEn: string; svg: React.ReactNode }) => {
                          const currentTab = new URLSearchParams(location.search).get('tab') || 'overview';
                          const targetPath = path || `/academy?tab=${tab}`;
                          const isActive = path
                            ? (location.pathname === path || location.pathname.startsWith('/academy/quiz'))
                            : (location.pathname === '/academy' && currentTab === tab);
                          return (
                            <Link key={tab} className={`sidebar__link${isActive ? ' active' : ''}`} to={targetPath}>
                              <div className={`icon-link ${isSidebarCollapsed ? '' : 'ml-4'}`}>
                                <span className="icon-link__icon" style={{ display: 'flex', alignItems: 'center' }}>{svg}</span>
                                <span className="icon-link__text">{lng === 'vi' ? labelVi : labelEn}</span>
                              </div>
                            </Link>
                          );
                        })}
                      </li>
                    </ul>
                  </div>
                </li>
              ) : (
                <li className="sidebar__item">
                  <div className={`sidebar__link sidebar__link--with-toggle ${location.pathname === "/academy" || location.pathname.startsWith("/academy/quiz") ? " active" : ""}`}>
                    <Link
                      className="icon-link"
                      to="/academy?tab=overview"
                      style={{ flex: 1, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 'inherit' }}
                    >
                      <i className="icon-link__icon fi fi--tutor"></i>
                      <span className="icon-link__text">{t('LUMIR Academy')}</span>
                    </Link>
                    <button
                      className={`sidebar__toggle-13w${isOpen13W ? ' sidebar__toggle-13w--open' : ''}`}
                      onClick={toggleCollapse13W}
                      title={isOpen13W ? 'Đóng menu' : 'Mở menu'}
                    >
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="m3 2 4 4-4 4" />
                      </svg>
                    </button>
                  </div>
                  <div className={`sidebar__collapse ${isOpen13W ? "show" : ""}`}>
                    <ul className="collapse__links">
                      <li className="collapse__item">
                        {[
                          // { tab: 'overview',  labelVi: 'Tổng quan',          labelEn: 'Overview',         svg: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{display:'block'}}><circle cx="12" cy="12" r="10"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/><line x1="2" y1="12" x2="22" y2="12"/></svg> },
                          //{ tab: 'dashboard', labelVi: 'Dashboard',           labelEn: 'Dashboard',        svg: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{display:'block'}}><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg> },
                          //{ tab: 'roadmap',   labelVi: 'Lộ trình 12 tháng',  labelEn: '12-Month Roadmap', svg: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{display:'block'}}><path d="M3 17l4-8 4 4 4-6 4 5"/><path d="M3 21h18"/></svg> },
                          //{ tab: 'kpi',       labelVi: 'KPI Tinh hoa',        labelEn: 'Elite KPIs',       svg: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{display:'block'}}><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg> },
                          { tab: 'checklist', labelVi: 'Checklist hàng ngày', labelEn: 'Daily Checklist', svg: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}><path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></svg> },
                          //{ tab: 'eval',      labelVi: 'Tự đánh giá Elite',   labelEn: 'Elite Self-Eval',  svg: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{display:'block'}}><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg> },
                          //{ tab: 'eliteplus', labelVi: 'Lộ trình Elite+',     labelEn: 'Elite+ Roadmap',   svg: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{display:'block'}}><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg> },
                          //{ tab: 'handbook',  labelVi: 'Cẩm nang sử dụng',   labelEn: 'User Handbook',    svg: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{display:'block'}}><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg> },
                          { tab: 'quiz', path: '/academy/quiz/dashboard', labelVi: 'Trắc Nghiệm', labelEn: 'Quiz', svg: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}><path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></svg> },

                        ].map(({ tab, path, labelVi, labelEn, svg }: { tab: string; path?: string; labelVi: string; labelEn: string; svg: React.ReactNode }) => {
                          const currentTab = new URLSearchParams(location.search).get('tab') || 'overview';
                          const targetPath = path || `/academy?tab=${tab}`;
                          const isActive = path
                            ? (location.pathname === path || location.pathname.startsWith('/academy/quiz'))
                            : (location.pathname === '/academy' && currentTab === tab);
                          return (
                            <Link key={tab} className={`sidebar__link${isActive ? ' active' : ''}`} to={targetPath}>
                              <div className={`icon-link ${isSidebarCollapsed ? '' : 'ml-4'}`}>
                                <span className="icon-link__icon" style={{ display: 'flex', alignItems: 'center' }}>{svg}</span>
                                <span className="icon-link__text">{lng === 'vi' ? labelVi : labelEn}</span>
                              </div>
                            </Link>
                          );
                        })}
                      </li>
                    </ul>
                  </div>
                </li>
              )
            }
            {isAdmin && (
              <li className="sidebar__item">
                <div className={`sidebar__link sidebar__link--with-toggle ${location.pathname === "/community" || location.pathname.startsWith("/community/") ? " active" : ""}`}>
                  <Link
                    className="icon-link"
                    to="/community"
                    style={{ flex: 1, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 'inherit' }}
                  >
                    <i className="icon-link__icon fi fi--tutor"></i>
                    <span className="icon-link__text">{t('LUMIR Community')}</span>
                  </Link>
                  <button
                    className={`sidebar__toggle-13w${isOpenCommunity ? ' sidebar__toggle-13w--open' : ''}`}
                    onClick={() => setIsOpenCommunity(!isOpenCommunity)}
                    title={isOpenCommunity ? 'Đóng menu' : 'Mở menu'}
                  >
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="m3 2 4 4-4 4" />
                    </svg>
                  </button>
                </div>
                <div className={`sidebar__collapse ${isOpenCommunity ? "show" : ""}`}>
                  <ul className="collapse__links">
                    <li className="collapse__item">
                      {[
                        { tab: 'leaderboard', labelVi: 'LeaderBoard', labelEn: 'LeaderBoard', svg: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" /></svg> },
                        { tab: 'manager-network', labelVi: 'Manager Network', labelEn: 'Manager Network', svg: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}><rect x="9" y="3" width="6" height="4" rx="1" /><rect x="2" y="17" width="6" height="4" rx="1" /><rect x="16" y="17" width="6" height="4" rx="1" /><path d="M12 7v5" /><path d="M5 12h14" /><path d="M5 12v5" /><path d="M19 12v5" /></svg> },
                      ].map(({ tab, labelVi, labelEn, svg }) => {
                        const isActive = location.pathname === `/community/${tab}`
                        return (
                          <Link key={tab} className={`sidebar__link${isActive ? ' active' : ''}`} to={`/community/${tab}`}>
                            <div className={`icon-link ${isSidebarCollapsed ? '' : 'ml-4'}`}>
                              <span className="icon-link__icon" style={{ display: 'flex', alignItems: 'center' }}>{svg}</span>
                              <span className="icon-link__text">{lng === 'vi' ? labelVi : labelEn}</span>
                            </div>
                          </Link>
                        );
                      })}
                    </li>
                  </ul>
                </div>
              </li>
            )}


            <li className="sidebar__item">
              <Link
                className={`sidebar__link${location.pathname === "/stores" ? " active" : ""}`}
                data-testid="navStore"
                id="menuStore"
                to="/stores"
              >
                <div className="icon-link">
                  <i className="icon-link__icon fi fi--premium" ></i>
                  <span className="icon-link__text">{t('Trading Bots Store')}</span>
                </div>
              </Link>
            </li>
            <li className="sidebar__item">
              <Link className={`sidebar__link${location.pathname === "/platform/download" ? " active" : ""}`} data-testid="navNotifications" data-notification-target="2" id="menuNotifications" to="/platform/download">
                <div className="icon-link">
                  <i className="icon-link__icon fi fi--download"></i>
                  <span className="icon-link__text">{t('Downloads')}</span>
                </div>
              </Link>
            </li>
            <li className="sidebar__item">
              <Link className={`sidebar__link${location.pathname === "/help" ? " active" : ""}`} data-testid="navHelp" id="menuHelp" to="/help">
                <div className="icon-link">
                  <i className="icon-link__icon fi fi--message"></i>
                  <span className="icon-link__text">{t('Help Center')}</span>
                </div>
              </Link>
            </li>
            <li className="sidebar__item">
              <Link className={`sidebar__link${location.pathname === "/library" || location.pathname.startsWith("/library/") ? " active" : ""}`} data-testid="navLibrary" id="menuLibrary" to="/library">
                <div className="icon-link">
                  <svg className="icon-link__icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
                  </svg>
                  <span className="icon-link__text">Thư viện</span>
                </div>
              </Link>
            </li>
            {/* <li className="sidebar__item !opacity-[15%] pointer-events-none">
              <a className="sidebar__link " data-testid="navFTMOTraders" data-notification-target="1" href="#">
                <div className="icon-link">
                  <i className="icon-link__icon fi fi--user-duo"></i>
                  <span className="icon-link__text">{t('LUMIR Traders')}</span>
                </div>
              </a>
            </li> */}
            {/* <li className="sidebar__item !opacity-[15%] pointer-events-none"><a className="sidebar__link " data-testid="navBilling" data-notification-target="4" id="menuBilling" href="#">
              <div className="icon-link">
                <i className="icon-link__icon fi fi--price-tag"></i>
                <span className="icon-link__text">{t('Billing')}</span>
              </div>

              <span data-notification-badge="" className="badge badge--sm badge--primary badge--outlined">3</span></a></li><li className="sidebar__item !opacity-[15%] pointer-events-none"><a className="sidebar__link " data-testid="navLeaderboard" id="menuLeaderboard" href="#">
                <div className="icon-link">
                  <i className="icon-link__icon fi fi--trophy"></i>
                  <span className="icon-link__text">{t('Leaderboard')}</span>
                </div>

              </a></li><li className="sidebar__item !opacity-[15%] pointer-events-none"><a className="sidebar__link " data-testid="navCertificates" id="menuCertificates" href="#">
                <div className="icon-link">
                  <i className="icon-link__icon fi fi--achievement-filled"></i>
                  <span className="icon-link__text">{t('Certificates')}</span>
                </div>

              
            </li>
            <li className="sidebar__item !opacity-[15%] pointer-events-none">
              <a className="sidebar__link " data-testid="navSocialMedia" id="menuSocialMedia" href="#">
                <div className="icon-link">
                  <i className="icon-link__icon fi fi--social-media"></i>
                  <span className="icon-link__text">{t('Social Media')}</span>
                </div>
              </a>
            </li> */}
            {/* <li className="sidebar__item">
              <div className="sidebar__separator "></div>
            </li>
            <li className="sidebar__item">
              <div
                className={`sidebar__link ${isOpen ? "" : "collapsed"}`}
                onClick={toggleCollapse}
                data-bs-toggle="collapse"
              >
                <div className="sidebar__collapse-heading">{t('Tools & Services')}</div>
              </div>
              <div className={`sidebar__collapse ${isOpen ? "show" : ""}`}>
                <ul className="collapse__links">
                  <li className="collapse__item">
                    <a className="sidebar__link !opacity-[15%] pointer-events-none" data-testid="navEconomicCalendar" id="menuEconomicCalendar" href="#">
                      <div className="icon-link">
                        <i className="icon-link__icon fi fi--ecal"></i>
                        <span className="icon-link__text">{t('Economic Calendar')}</span>
                      </div>
                    </a>
                    <a className="sidebar__link locked pointer-events-none" data-testid="navSymbolsUpdates" id="menuSymbolsUpdated" href="#" target="_blank">
                      <div className="icon-link">
                        <i className="icon-link__icon fi fi--list-reload"></i>
                        <span className="icon-link__text">{t('Symbols & Updates')}</span>
                      </div>
                    </a>
                    <a className="sidebar__link locked pointer-events-none" data-testid="navTicker" id="menuTicker" href="#">
                      <div className="icon-link">
                        <i className="icon-link__icon fi fi--ticker"></i>
                        <span className="icon-link__text">Ticker</span>
                      </div>
                    </a>
                    <a className="sidebar__link locked" data-testid="navClientAnalysis" id="menuClientAnalysis" href="#">
                      <div className="icon-link">
                        <i className="icon-link__icon fi fi--person-in-loupe"></i>
                        <span className="icon-link__text">{t("Trader's Analysis")}</span>
                      </div>
                    </a>
                    <a className="sidebar__link locked pointer-events-none" id="menuTimezoneConverter" href="#">
                      <div className="icon-link">
                        <i className="icon-link__icon fi fi--map-pointer"></i>
                        <span className="icon-link__text">{t('Timezone Converter')}</span>
                      </div>
                    </a>
                    <a className="sidebar__link locked pointer-events-none" data-testid="navPartnershipDeals" id="menuPartnershipDeals" data-bs-toggle="tooltip" data-bs-custom-class="tooltip--sidebar-item" data-bs-placement="right" href="#">
                      <div className="icon-link">
                        <i className="icon-link__icon fi fi--deal"></i>
                        <span className="icon-link__text">{t('Partnership Deals')}</span>
                      </div>
                      <i className="link-tooltip link-tooltip--locked"></i>
                    </a>
                    <a className="sidebar__link locked pointer-events-none" data-testid="navStatisticalApp" id="menuStatisticalApp" data-bs-toggle="tooltip" data-bs-custom-class="tooltip--sidebar-item" data-bs-placement="right" href="#">
                      <div className="icon-link">
                        <i className="icon-link__icon fi fi--stats-bordered"></i>
                        <span className="icon-link__text">{t('Statistical App')}</span>
                      </div>
                      <i className="link-tooltip link-tooltip--locked"></i>
                    </a>
                    <a className="sidebar__link locked pointer-events-none" data-testid="navSizePositionCalculator" id="menuSizePositionCalculator" data-bs-toggle="tooltip" data-bs-custom-class="tooltip--sidebar-item" data-bs-placement="right" href="#">
                      <div className="icon-link">
                        <i className="icon-link__icon fi fi--calc"></i>
                        <span className="icon-link__text">{t('Statistical App')}</span>
                      </div>
                      <i className="link-tooltip link-tooltip--locked"></i>
                    </a>
                    <a className="sidebar__link locked pointer-events-none" data-testid="navMentorApp" id="menuMentorApp" data-bs-toggle="tooltip" data-bs-custom-class="tooltip--sidebar-item" data-bs-placement="right" href="#">
                      <div className="icon-link">
                        <i className="icon-link__icon fi fi--presentation-checked"></i>
                        <span className="icon-link__text">{t('Mentor App')}</span>
                      </div>
                      <i className="link-tooltip link-tooltip--locked"></i>
                    </a>
                    <a className="sidebar__link locked pointer-events-none" data-testid="navPerformanceCoach" id="menuPerformanceCoach" data-bs-toggle="tooltip" data-bs-custom-class="tooltip--sidebar-item" data-bs-placement="right" href="#">
                      <div className="icon-link">
                        <i className="icon-link__icon fi fi--performance-coaching"></i>
                        <span className="icon-link__text">{t('Performance Coaching')}</span>
                      </div>
                      <i className="link-tooltip link-tooltip--locked"></i>
                    </a>
                  </li>
                </ul>
              </div>
            </li> */}
            {/* <li className="sidebar__item"><div className="sidebar__separator "></div></li><li className="sidebar__item">
              <div
                className={`sidebar__link ${isOpenSP ? "" : "collapsed"}`}
                onClick={toggleCollapseSP}
                data-bs-toggle="collapse"
              >
                <div className="sidebar__collapse-heading">{t('Support')}</div>

              </div>
              <div className={`sidebar__collapse ${isOpenSP ? "show" : ""}`}>
                <ul className="collapse__links">
                  <li className="collapse__item">
                    <a className="sidebar__link locked pointer-events-none" data-testid="navCustomerSupport" data-notification-target="2" id="menuCustomerSupport" href="#">
                      <div className="icon-link">
                        <i className="icon-link__icon fi fi--message"></i>
                        <span className="icon-link__text">{t('Send message')}</span>
                      </div>

                    </a><a className="sidebar__link locked pointer-events-none" data-testid="navLiveChat" id="openLiveChat" href="#">
                      <div className="icon-link">
                        <i className="icon-link__icon fi fi--chat"></i>
                        <span className="icon-link__text">{t('Live Chat')}</span>
                      </div>

                    </a><a className="sidebar__link locked pointer-events-none" data-testid="navDiscord" href="#" target="_blank">
                      <div className="icon-link">
                        <i className="icon-link__icon fi fi--discord"></i>
                        <span className="icon-link__text">{t('Discord')}</span>
                      </div>

                    </a>
                  </li>
                </ul>
              </div>
            </li> */}
            {/* <li className="sidebar__item"><div className="sidebar__separator "></div></li><li className="sidebar__item">
              <div
                className={`sidebar__link ${isOpenUD ? "" : "collapsed"}`}
                onClick={toggleCollapseUD}
                data-bs-toggle="collapse"
              >

                <div className="sidebar__collapse-heading">{t('Mobile app')}</div>

              </div>
              <div className={`sidebar__collapse ${isOpenUD ? "show" : ""}`}>
                <ul className="collapse__links">
                  <li className="collapse__item">
                    <a className="sidebar__link locked pointer-events-none" href="#">
                      <div className="icon-link">
                        <i className="icon-link__icon fi fi--apple"></i>
                        <span className="icon-link__text">{t('Download on App Store')}</span>
                      </div>

                    </a>
                    <a href="#" className="sidebar__link locked pointer-events-none">
                      <div className="icon-link">
                        <i className="icon-link__icon fi fi--google-play"></i>
                        <span className="icon-link__text">{t('Download on Google Play')}</span>
                      </div>
                    </a>
                  </li>
                </ul>
              </div>
            </li> */}
            <li className="sidebar__item">
              <div className="sidebar__separator "></div>
            </li>
            <li className="sidebar__item" onClick={toggleTheme}>
              <a className="sidebar__link" href="#" onClick={(e) => e.preventDefault()}>
                <div className="icon-link">
                  <i className={`icon-link__icon illustration illustration__${isDarkMode ? 'nightlife' : 'daylight'}`}></i>
                  <span className="icon-link__text">
                    {isDarkMode ? t('Light mode') : t('Dark mode')}
                  </span>
                </div>
              </a>
            </li>
            <li className="sidebar__item">
              <a className="sidebar__link " data-testid="navBackToWebsite" id="menuBackToWebsite" href="#">
                <div className="icon-link">
                  <i className="icon-link__icon fi fi--exit"></i>
                  <span className="icon-link__text">{t('Back to website')}</span>
                </div>
              </a>
            </li>
            <li className="sidebar__item">
              <div
                className={`select select--animated select--lang-switcher ${isOpenLang ? "select--open" : ""}`} data-for="language-select"
                onClick={toggleCollapseLang}
              >
                <div className='select__input sidebar__link'>
                  <div className={`lang-switcher__icon lang-switcher__icon--${lng}`}></div>
                  <span className="lang-switcher__text">
                    {lng === 'en' ? 'English' : 'Tiếng Việt'}
                  </span>
                </div>
                <div className="select__options">
                  {/* <div className="select__option " data-value="de">
                      <div className="lang-switcher__icon lang-switcher__icon--de"></div>Deutsch
                    </div>
                    <div className="select__option " data-value="cs">
                      <div className="lang-switcher__icon lang-switcher__icon--cs"></div>Čeština
                    </div> */}
                  <div className="select__option" onClick={() => changeLanguage('vi')}>
                    <div className="lang-switcher__icon lang-switcher__icon--vi"></div>
                    <span className="lang-switcher__option-text">Tiếng Việt</span>
                  </div>
                  <div className="select__option" onClick={() => changeLanguage('en')}>
                    <div className="lang-switcher__icon lang-switcher__icon--en"></div>
                    <span className="lang-switcher__option-text">English</span>
                  </div>

                  {/* <div className="select__option " data-value="fr">
                      <div className="lang-switcher__icon lang-switcher__icon--fr"></div>Français
                    </div>
                    <div className="select__option " data-value="es">
                      <div className="lang-switcher__icon lang-switcher__icon--es"></div>Español
                    </div>
                    <div className="select__option " data-value="it">
                      <div className="lang-switcher__icon lang-switcher__icon--it"></div>Italiano
                    </div>
                    <div className="select__option " data-value="pt">
                      <div className="lang-switcher__icon lang-switcher__icon--pt"></div>Português
                    </div> */}
                </div>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div >
  );
};

export default Sidebar;
